import {useCallback, useState} from 'react';
import TabItem from '../../../@types/tab-item';
import {ResourceType} from '../../../@types/resource-type';
import {
    createNewTab, filterTabsAfterDeletion, removeTabAndReindex, synchronizeRelatedTabs, updateTabInArray
} from './tab-editor-utils';

export interface TabState {
    tabs: TabItem[];
    activeIndex: number;
}

export interface TabStateActions {
    addTab: (resourceType: ResourceType, object: any, getAdditionalResource?: (type: ResourceType) => any) => void;
    removeTab: (index: number) => void;
    updateTab: (index: number, updates: Partial<TabItem>) => void;
    updateAndSynchronizeTab: (index: number, updates: Partial<TabItem>, type: ResourceType) => void;
    setActiveIndex: (index: number) => void;
    findTab: (resourceType: ResourceType, objectId: string | number) => TabItem | undefined;
    findTabIndex: (resourceType: ResourceType, objectId: string | number) => number;
    removeTabsForDeletedObject: (deletedObjectId: string | number, deletedChildrenIds?: (string | number)[]) => void;
    synchronizeTabs: (updatedObject: { objectType: ResourceType, object: any }) => void;
    replaceAllTabs: (newTabs: TabItem[]) => void;
}

/**
 * Custom hook for managing tab state
 */
export function useTabState(
    initialTabs: TabItem[] = [],
    initialActiveIndex: number = 0,
    onTabsChange?: (tabs: TabItem[]) => void,
    onActiveIndexChange?: (index: number) => void
): TabState & TabStateActions {
    const [tabs, setTabs] = useState<TabItem[]>(initialTabs);
    const [activeIndex, setActiveIndexState] = useState<number>(initialActiveIndex);

    // Add a new tab
    const addTab = useCallback((
        resourceType: ResourceType,
        object: any,
        getAdditionalResource?: (type: ResourceType) => any
    ) => {
        const newTab = createNewTab(resourceType, object, tabs.length, getAdditionalResource);
        const updatedTabs = [...tabs, newTab];
        
        setTabs(updatedTabs);
        setActiveIndexState(newTab.index);
        
        onTabsChange?.(updatedTabs);
        onActiveIndexChange?.(newTab.index);
    }, [tabs, onTabsChange, onActiveIndexChange]);

    // Remove a tab by index
    const removeTab = useCallback((index: number) => {
        const tabToDelete = tabs.find(tab => tab.index === index);
        if (!tabToDelete) return;

        const { newTabs, newActiveIndex } = removeTabAndReindex(tabs, tabToDelete, activeIndex);

        setTabs(newTabs);
        setActiveIndexState(newActiveIndex);

        onTabsChange?.(newTabs);
        onActiveIndexChange?.(newActiveIndex);
    }, [tabs, activeIndex, onTabsChange, onActiveIndexChange]);

    // Update a tab's properties
    const updateTab = useCallback((index: number, updates: Partial<TabItem>) => {
        const tabToUpdate = tabs.find(tab => tab.index === index);
        if (!tabToUpdate) return;

        const updatedTabs = updateTabInArray(tabs, tabToUpdate, updates);
        
        setTabs(updatedTabs);
        onTabsChange?.(updatedTabs);
    }, [tabs, onTabsChange]);

    const updateAndSynchronizeTab = useCallback((index: number, updates: Partial<TabItem>, type: ResourceType) => {
        const tabToUpdate = tabs.find(tab => tab.index === index);
        if (!tabToUpdate) return;

        const updatedTabs = updateTabInArray(tabs, tabToUpdate, updates);
       
        const synchronizedTabs = synchronizeRelatedTabs(updatedTabs, {objectType: type, object: updates.objectToEdit});
        
        setTabs(synchronizedTabs);
        onTabsChange?.(synchronizedTabs);
    }, [tabs, onTabsChange]);


    // Set the active tab index
    const setActiveIndex = useCallback((index: number) => {
        if (index >= 0 && index < tabs.length) {
            setActiveIndexState(index);
            onActiveIndexChange?.(index);
        }
    }, [tabs.length, onActiveIndexChange]);

    // Find a tab by resource type and object ID
    const findTab = useCallback((resourceType: ResourceType, objectId: string | number): TabItem | undefined => {
        return tabs.find(tab => tab.type === resourceType && tab.docId == objectId);
    }, [tabs]);

    // Find a tab index by resource type and object ID
    const findTabIndex = useCallback((resourceType: ResourceType, objectId: string | number): number => {
        const tab = findTab(resourceType, objectId);
        return tab ? tab.index : -1;
    }, [findTab]);

    // Remove tabs for a deleted workspace object
    const removeTabsForDeletedObject = useCallback((
        deletedObjectId: string | number,
        deletedChildrenIds: (string | number)[] = []
    ) => {
        const filteredTabs = filterTabsAfterDeletion(tabs, deletedObjectId, deletedChildrenIds);
        
        // Calculate new active index
        let newActiveIndex = activeIndex;
        if (activeIndex >= filteredTabs.length) {
            newActiveIndex = filteredTabs.length > 0 ? filteredTabs.length - 1 : 0;
        }
        
        setTabs(filteredTabs);
        setActiveIndexState(newActiveIndex);
        
        onTabsChange?.(filteredTabs);
        onActiveIndexChange?.(newActiveIndex);
    }, [tabs, activeIndex, onTabsChange, onActiveIndexChange]);

    // Synchronize related tabs when an object is updated
    const synchronizeTabs = useCallback((
        updatedObject: { objectType: ResourceType, object: any }
    ) => {
        const synchronizedTabs = synchronizeRelatedTabs(tabs, updatedObject);
        
        setTabs(synchronizedTabs);
        onTabsChange?.(synchronizedTabs);
    }, [tabs, onTabsChange]);

    // Replace all tabs (useful for loading from storage)
    const replaceAllTabs = useCallback((newTabs: TabItem[]) => {
        setTabs(newTabs);
        onTabsChange?.(newTabs);
    }, [onTabsChange]);

    return {
        tabs,
        activeIndex,
        addTab,
        removeTab,
        updateTab,
        updateAndSynchronizeTab,
        setActiveIndex,
        findTab,
        findTabIndex,
        removeTabsForDeletedObject,
        synchronizeTabs,
        replaceAllTabs
    };
}